<template>
    <div class="donut-chart-wrapper">
        <div class="donut-chart-container">
            <canvas ref="chart"></canvas>
            <div class="center-label">
                <span class="center-value" :style="{ fontSize: normalizeFontSize(22) }">{{ displayCenterValue }}</span>
                <span class="center-text" :style="{ fontSize: normalizeFontSize(13) }">{{ displayCenterLabel }}</span>
            </div>
        </div>
        <div class="donut-legend" v-if="showLegend">
            <div
                v-for="(item, index) in legendData"
                :key="index"
                class="legend-row"
                :class="{ active: selectedIndex === index }"
                @click="selectItem(index)"
            >
                <div class="d-flex align-items-center gap-2">
                    <div class="legend-marker" :style="{ borderColor: item.color }"></div>
                    <span class="legend-label" :style="{ fontSize: normalizeFontSize(14) }">{{ item.label }}</span>
                </div>
                <span class="legend-value poppinsSemiBold" :style="{ fontSize: normalizeFontSize(14) }">{{
                    item.formattedValue
                }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import Chart from 'chart.js/auto'
import normalizeSize from '../../../../utils/normalizeSize'
import normalizeFontSize from '../../../../utils/normalizeFontSize'

export default {
    name: 'DonutChart',
    props: {
        data: {
            type: Array,
            required: true,
            // Expected format: [{ label: 'Item', value: 25, color: '#4A3AFF' }]
        },
        centerLabel: {
            type: String,
            default: '',
        },
        centerValue: {
            type: String,
            default: '',
        },
        showLegend: {
            type: Boolean,
            default: true,
        },
        valueFormat: {
            type: String,
            default: 'percent', // 'percent', 'number', 'currency'
        },
    },
    data() {
        return {
            chart: null,
            selectedIndex: 0, // Default to first item
        }
    },
    computed: {
        legendData() {
            const total = this.data.reduce((sum, item) => sum + item.value, 0)
            return this.data.map((item) => ({
                ...item,
                formattedValue: this.formatValue(item.value, total),
            }))
        },
        displayCenterLabel() {
            if (this.selectedIndex !== null && this.data[this.selectedIndex]) {
                return this.data[this.selectedIndex].label
            }
            return this.centerLabel
        },
        displayCenterValue() {
            if (this.selectedIndex !== null && this.data[this.selectedIndex]) {
                const total = this.data.reduce((sum, item) => sum + item.value, 0)
                const value = this.data[this.selectedIndex].value
                return total > 0 ? `${Math.round((value / total) * 100)}%` : '0%'
            }
            return this.centerValue
        },
    },
    watch: {
        data: {
            deep: true,
            handler() {
                this.updateChart()
            },
        },
    },
    mounted() {
        this.createChart()
    },
    beforeDestroy() {
        if (this.chart) {
            this.chart.destroy()
        }
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        selectItem(index) {
            this.selectedIndex = index
            // Highlight the selected segment
            if (this.chart) {
                this.chart.setActiveElements([{ datasetIndex: 0, index: index }])
                this.chart.update()
            }
        },
        formatValue(value, total) {
            switch (this.valueFormat) {
                case 'percent':
                    return total > 0 ? `${((value / total) * 100).toFixed(2)}%` : '0%'
                case 'currency':
                    return `${value.toLocaleString(this.$currencyLocale?.replace('_', '-') || 'fr-FR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    })}${this.$symbolCurrency}`
                default:
                    return value.toLocaleString(this.$currencyLocale?.replace('_', '-') || 'fr-FR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    })
            }
        },
        createChart() {
            const ctx = this.$refs.chart.getContext('2d')

            this.chart = new Chart(ctx, {
                type: 'doughnut',
                data: {
                    labels: this.data.map((item) => item.label),
                    datasets: [
                        {
                            data: this.data.map((item) => item.value),
                            backgroundColor: this.data.map((item) => item.color),
                            borderColor: this.data.map((item) => item.color),
                            borderWidth: 0,
                            cutout: '70%',
                            hoverOffset: 20,
                            borderRadius: 0,
                            hoverBorderRadius: 2,
                            spacing: 0,
                            hoverBorderWidth: 18,
                            hoverBorderColor: this.data.map((item) => item.color),
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: true,
                    layout: {
                        padding: 30,
                    },
                    plugins: {
                        legend: {
                            display: false,
                        },
                        tooltip: {
                            backgroundColor: '#1E1B39',
                            titleColor: '#FFF',
                            bodyColor: '#FFF',
                            padding: 10,
                            cornerRadius: 8,
                            callbacks: {
                                label: (context) => {
                                    const total = context.dataset.data.reduce((a, b) => a + b, 0)
                                    const percent = ((context.parsed / total) * 100).toFixed(1)
                                    return `${context.label}: ${percent}%`
                                },
                            },
                        },
                    },
                    onClick: (event, elements) => {
                        if (elements.length > 0) {
                            this.selectItem(elements[0].index)
                        }
                    },
                },
            })
        },
        updateChart() {
            if (this.chart) {
                this.chart.data.labels = this.data.map((item) => item.label)
                this.chart.data.datasets[0].data = this.data.map((item) => item.value)
                this.chart.data.datasets[0].backgroundColor = this.data.map((item) => item.color)
                this.chart.data.datasets[0].borderColor = this.data.map((item) => item.color)
                this.chart.data.datasets[0].hoverBorderColor = this.data.map((item) => item.color)
                this.chart.update()
            }
        },
    },
}
</script>

<style scoped>
.donut-chart-wrapper {
    display: flex;
    align-items: center;
    gap: 30px;
    width: 100%;
    justify-content: space-around;
}

.donut-chart-container {
    position: relative;
    width: 180px;
    height: 180px;
}

.center-label {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
}

.center-value {
    display: block;
    color: #fff;
    font-weight: 600;
}

.center-text {
    display: block;
    color: #fff;
    letter-spacing: -0.03em;
}

.donut-legend {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.legend-row {
    display: flex;
    align-items: center;
    gap: 10px;
    justify-content: space-between;
}

.legend-marker {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 3px solid;
    background: transparent;
}

.legend-label {
    color: #fff;
    min-width: 80px;
}

.legend-value {
    color: #fff;
    text-align: right;
}
</style>
